// src/index.ts
import { statusConfig } from "@grove/types";
function youtubeVideoId(url) {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i
  );
  return m ? m[1] : null;
}
function formatDuration(seconds) {
  if (seconds == null || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = Math.floor(seconds % 60);
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}
function formatPages(pages) {
  if (pages == null || pages <= 0) return null;
  return pages === 1 ? "1 page" : `${pages} pages`;
}
function shareSnippet(subject) {
  const byline = [subject.author && `By ${subject.author}`, subject.siteName].filter(Boolean).join(" \xB7 ");
  return byline ? `\u201C${subject.title}\u201D \u2014 ${byline}` : `\u201C${subject.title}\u201D`;
}
function activeSegmentIndex(segments, t) {
  let lo = 0;
  let hi = segments.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = lo + hi >> 1;
    if (segments[mid].start <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}
var ApiError = class extends Error {
  constructor(status, body) {
    super(`API ${status}: ${body || "request failed"}`);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
};
var AuthError = class extends ApiError {
  constructor(body = "") {
    super(401, body);
    this.name = "AuthError";
  }
};
function trialErrorFrom(e) {
  if (!(e instanceof ApiError) || e.status !== 403) return null;
  try {
    const body = JSON.parse(e.body);
    return body?.code === "TRIAL_EXPIRED" ? { trialEndedAt: body.trialEndedAt ?? null } : null;
  } catch {
    return null;
  }
}
function createApiClient(opts) {
  const fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  async function request(path, init = {}) {
    const authHeaders = await opts.getAuthHeaders?.() ?? {};
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
      ...init.headers ?? {}
    };
    const res = await fetchImpl(`${opts.baseUrl}${path}`, { ...init, headers });
    if (res.status === 401) {
      opts.onAuthError?.();
      const body = await res.text().catch(() => "");
      throw new AuthError(body);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new ApiError(res.status, body || res.statusText);
    }
    if (res.status === 204) return void 0;
    return res.json();
  }
  return {
    // ── Articles ─────────────────────────────────────────────────────────────
    listArticles: (params) => {
      const qs = new URLSearchParams();
      if (params?.status) {
        const value = Array.isArray(params.status) ? params.status.join(",") : params.status;
        qs.set("status", value);
      }
      if (params?.since) qs.set("since", params.since);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.sort) qs.set("sort", params.sort);
      const q = qs.toString();
      return request(`/api/articles${q ? `?${q}` : ""}`);
    },
    // `touch: true` stamps lastAccessedAt server-side — pass it for real reader
    // opens, omit it for background/bulk fetches so the "recently opened" sort
    // isn't polluted.
    getArticle: (id, opts2) => request(`/api/articles/${id}${opts2?.touch ? "?touch=1" : ""}`),
    getArticleByUrl: (url) => request(`/api/articles/by-url?url=${encodeURIComponent(url)}`),
    clipArticle: (body) => request("/api/articles/clip", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    updateArticle: (id, patch) => request(`/api/articles/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
    // Relative path for a stored PDF's authenticated streaming endpoint.
    // Consumers prepend their base URL and attach their own auth (cookies /
    // Bearer) — the client can't fetch it here because the response is binary.
    articleFilePath: (id) => `/api/articles/${id}/file`,
    deleteArticle: (id) => request(`/api/articles/${id}`, { method: "DELETE" }),
    // ── Sharing ──────────────────────────────────────────────────────────────
    // Every share surface (copy link, email, social, OS share sheet) hands out
    // the Grove share URL, never the raw source URL — the public page is where
    // recipients meet Grove.
    /** Creates the article's public link, or returns the existing one. */
    createShareLink: (articleId) => request(`/api/articles/${articleId}/share`, { method: "POST" }),
    shareByEmail: (articleId, body) => request(`/api/articles/${articleId}/share/email`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    revokeShareLink: (articleId) => request(`/api/articles/${articleId}/share`, { method: "DELETE" }),
    /** Anonymous read of a public share. No session required. */
    getPublicShare: (token) => request(`/api/shares/${encodeURIComponent(token)}`),
    // ── Auth / profile ───────────────────────────────────────────────────────
    getProfile: () => request("/api/auth/me"),
    /** Trial/subscription state — backed by the custom /api/user/quota route
     *  (kept at the old path so every client needs one coordinated change,
     *  not two), not Better Auth's /api/auth/me. */
    getTrialStatus: () => request("/api/user/quota"),
    updateProfile: (patch) => request("/api/auth/me", {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
    // ── Highlights ───────────────────────────────────────────────────────────
    /** Append one highlight. Never replaces the others — no clobber race. */
    addHighlight: (articleId, body) => request(`/api/articles/${articleId}/highlights`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    /** Annotate (or re-word) one highlight. An empty note clears it. */
    updateHighlight: (id, patch) => request(`/api/highlights/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
    deleteHighlight: (id) => request(`/api/highlights/${id}`, { method: "DELETE" }),
    /**
     * Everything you've kept, newest first, across every article.
     * `annotated: true` narrows it to the ones you wrote something about.
     */
    listHighlights: (opts2 = {}) => {
      const params = new URLSearchParams();
      if (opts2.since) params.set("since", opts2.since);
      if (opts2.limit) params.set("limit", String(opts2.limit));
      if (opts2.articleId) params.set("articleId", opts2.articleId);
      if (opts2.annotated) params.set("annotated", "1");
      const qs = params.toString();
      return request(`/api/highlights${qs ? `?${qs}` : ""}`);
    },
    getSessions: () => request("/api/auth/sessions"),
    revokeSession: (id) => request(`/api/auth/sessions/${id}`, { method: "DELETE" }),
    // ── Personal access tokens ───────────────────────────────────────────────
    listTokens: () => request("/api/tokens"),
    createToken: (body) => request("/api/tokens", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    revokeToken: (id) => request(`/api/tokens/${id}`, { method: "DELETE" }),
    // ── Discover ────────────────────────────────────────────────────────────
    discoverTopThisWeek: () => request("/api/discover/top-this-week"),
    discoverPeopleLikeYou: () => request("/api/discover/people-like-you"),
    discoverFromYourSources: () => request("/api/discover/from-your-sources"),
    dismissDiscoverItem: (url) => request("/api/discover/dismiss", {
      method: "POST",
      body: JSON.stringify({ url })
    })
  };
}
export {
  ApiError,
  AuthError,
  activeSegmentIndex,
  createApiClient,
  formatDuration,
  formatPages,
  shareSnippet,
  statusConfig,
  trialErrorFrom,
  youtubeVideoId
};
